/**
 * Button style objects – base, sizes, variants, hover, active, disabled.
 * Mirrors Html.css button styles for the new Button component.
 */

export const baseStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  fontWeight: 500,
  textAlign: "center",
  textDecoration: "none",
  border: "none",
  borderRadius: 5,
  cursor: "pointer",
  transition: "background-color 0.3s ease, border-color 0.3s ease, opacity 0.2s ease",
  whiteSpace: "nowrap",
  width: "fit-content",
  outline: "none",
  paddingTop: 8,
  paddingBottom: 8,
  paddingLeft: 15,
  paddingRight: 15,
  fontSize: 12,
};

export const sizeStyles = {
  sm: { padding: "6px 12px", fontSize: 12, minHeight: 28 },
  md: { padding: "8px 16px", fontSize: 14, minHeight: 36 },
  lg: { padding: "10px 20px", fontSize: 15, minHeight: 42 },
};

/** IconButton: square dimensions per size (same width/height) */
export const iconButtonSizeStyles = {
  sm: { minWidth: 28, width: 28, height: 28, minHeight: 28, padding: 6 },
  md: { minWidth: 36, width: 36, height: 36, minHeight: 36, padding: 8 },
  lg: { minWidth: 42, width: 42, height: 42, minHeight: 42, padding: 10 },
};

/** Variant styles (default state) */
export const variantStyles = {
  /* Primary */
  primary: {
    backgroundColor: "#007bff",
    color: "white",
    border: "none",
    fontWeight: "bold",
  },
  primaryOutline: {
    background: "transparent",
    color: "#007bff",
    border: "1px solid #007bff",
  },
  /* Default / neutral */
  default: {
    backgroundColor: "#e2e6ea",
    color: "#333",
    border: "1px solid #ccc",
  },
  defaultOutline: {
    background: "transparent",
    border: "1px solid rgba(19, 21, 32, 0.373)",
    color: "rgba(19, 21, 32, 0.605)",
  },
  /* Secondary (Bootstrap-style) */
  secondary: {
    backgroundColor: "#6c757d",
    color: "white",
    border: "1px solid #6c757d",
  },
  /* Dark */
  dark: {
    border: "1px solid #dee2e6",
    backgroundColor: "black",
    color: "white",
  },
  darkOutline: {
    border: "1px solid black",
    background: "transparent",
  },
  /* Download (black) */
  download: {
    border: "1px solid #dee2e6",
    padding: "8px 5px",
    backgroundColor: "black",
    color: "white",
  },
  downloadOutline: {
    border: "1px solid #dee2e6",
    padding: "6px 5px",
    background: "transparent",
  },
  /* Success (green) */
  success: {
    border: "1px solid #dee2e6",
    padding: "8px 5px",
    backgroundColor: "green",
    color: "white",
  },
  successOutline: {
    border: "1px solid green",
    padding: "6px 5px",
    background: "transparent",
  },
  /* Confirmation (red) */
  confirmation: {
    border: "1px solid #dee2e6",
    padding: "8px 5px",
    backgroundColor: "red",
    color: "white",
  },
  confirmationOutline: {
    border: "1px solid red",
    padding: "6px 5px",
    background: "transparent",
  },
  /* Save (purple gradient) */
  save: {
    backgroundImage: "linear-gradient(180deg, rgb(132, 130, 249), rgb(81, 48, 255))",
    color: "white",
    borderRadius: 24,
    minWidth: 80,
    border: "none",
  },
  saveOutline: {
    background: "transparent",
    color: "#5537ff",
    borderRadius: 24,
    border: "1px solid #5537ff",
  },
  /* Reject (red gradient) */
  reject: {
    backgroundImage: "linear-gradient(180deg, rgb(239, 68, 68), rgb(220, 38, 38))",
    color: "white",
    borderRadius: 24,
    minWidth: 80,
    border: "none",
  },
  rejectOutline: {
    background: "transparent",
    color: "#dc2626",
    borderRadius: 24,
    border: "1px solid #dc2626",
  },
  /* Transfer (blue gradient) */
  transfer: {
    backgroundImage: "linear-gradient(180deg, rgb(59, 130, 246), rgb(37, 99, 235))",
    color: "white",
    borderRadius: 24,
    minWidth: 80,
    border: "none",
  },
  transferOutline: {
    background: "transparent",
    color: "#2563eb",
    borderRadius: 24,
    border: "1px solid #2563eb",
  },
  /* Discard */
  discard: {
    backgroundColor: "rgb(209, 209, 209)",
    color: "black",
    borderRadius: 24,
    minWidth: 80,
    border: "none",
  },
  /* Request */
  request: {
    background: "transparent",
    color: "#5537ff",
    borderRadius: 24,
    border: "1px solid #5537ff",
  },
  /* Detail page */
  detailPage: {
    border: "1px solid #5537ff",
    borderRadius: 17,
    width: "100%",
    maxWidth: 200,
    fontWeight: 500,
    fontSize: 15,
    lineHeight: "18px",
    color: "rgb(47, 47, 47)",
    background: "transparent",
  },
  /* History */
  history: {
    border: "1px solid #616161",
    fontWeight: 500,
    lineHeight: "16px",
    fontSize: 13,
    color: "rgb(97, 97, 97)",
    borderRadius: 24,
    padding: "5.5px 10px",
    background: "transparent",
  },
  /* Ghost / Danger (common UI) */
  ghost: {
    background: "transparent",
    color: "#333",
    border: "none",
  },
  danger: {
    backgroundColor: "#dc3545",
    color: "white",
    border: "1px solid #dc3545",
  },
  imageView: {
    border: "1px solid rgb(52, 199, 89)",
    borderRadius: 17,
    width: "100%",
    maxWidth: 200,
    fontWeight: 500,
    fontSize: 15,
    lineHeight: "18px",
    color: "rgb(52, 199, 89)",
    background: "transparent",
  },
};

/** Hover styles per variant */
export const variantHoverStyles = {
  primary: { backgroundColor: "#0056b3" },
  primaryOutline: { backgroundColor: "rgba(0, 123, 255, 0.08)" },
  default: { backgroundColor: "#a1a1a178", borderColor: "#adb5bd" },
  defaultOutline: { backgroundColor: "rgba(19, 21, 32, 0.04)" },
  secondary: { backgroundColor: "#5a6268", borderColor: "#545b62" },
  dark: { backgroundColor: "#333" },
  darkOutline: { backgroundColor: "rgba(0, 0, 0, 0.06)" },
  download: { backgroundColor: "#333" },
  downloadOutline: { backgroundColor: "rgba(0, 0, 0, 0.04)" },
  success: { backgroundColor: "#0d7a0d" },
  successOutline: { backgroundColor: "rgba(0, 128, 0, 0.08)" },
  confirmation: { backgroundColor: "#cc0000" },
  confirmationOutline: { backgroundColor: "rgba(255, 0, 0, 0.08)" },
  save: { opacity: 0.92 },
  saveOutline: { backgroundColor: "rgba(85, 55, 255, 0.08)" },
  reject: { opacity: 0.92 },
  rejectOutline: { backgroundColor: "rgba(220, 38, 38, 0.08)" },
  transfer: { opacity: 0.92 },
  transferOutline: { backgroundColor: "rgba(37, 99, 235, 0.08)" },
  discard: { backgroundColor: "rgb(189, 189, 189)" },
  request: { backgroundColor: "rgba(85, 55, 255, 0.08)" },
  detailPage: {},
  history: { backgroundColor: "rgba(97, 97, 97, 0.08)" },
  ghost: { backgroundColor: "rgba(0, 0, 0, 0.06)" },
  danger: { backgroundColor: "#c82333", borderColor: "#bd2130" },
};

/** Active (pressed) styles per variant */
export const variantActiveStyles = {
  primary: { backgroundColor: "#004085" },
  primaryOutline: { backgroundColor: "rgba(0, 123, 255, 0.12)" },
  default: { backgroundColor: "#8c8c8c78" },
  defaultOutline: { backgroundColor: "rgba(19, 21, 32, 0.08)" },
  secondary: { backgroundColor: "#545b62" },
  dark: { backgroundColor: "#000" },
  darkOutline: { backgroundColor: "rgba(0, 0, 0, 0.1)" },
  ghost: { backgroundColor: "rgba(0, 0, 0, 0.1)" },
  danger: { backgroundColor: "#bd2130" },
};

/** Disabled styles per variant (fallback to disabledStyle when not defined) */
export const variantDisabledStyles = {
  primary: {},
  primaryOutline: {},
  default: {},
  defaultOutline: {},
  secondary: {},
  dark: {},
  darkOutline: {},
  download: {},
  downloadOutline: {},
  success: {},
  successOutline: {},
  confirmation: {},
  confirmationOutline: {},
  save: { cursor: "not-allowed", opacity: 0.8 },
  saveOutline: { background: "transparent", cursor: "not-allowed" },
  reject: { cursor: "not-allowed", opacity: 0.8 },
  rejectOutline: { background: "transparent", cursor: "not-allowed" },
  transfer: { cursor: "not-allowed", opacity: 0.8 },
  transferOutline: { background: "transparent", cursor: "not-allowed" },
  discard: {
    backgroundColor: "rgba(223, 224, 226, 0.77)",
    color: "rgb(149, 150, 152)",
    cursor: "not-allowed",
  },
  request: { cursor: "not-allowed" },
  detailPage: { cursor: "not-allowed", opacity: 0.6 },
  history: {},
  ghost: {},
  danger: {},
  imageView: { cursor: "not-allowed", opacity: 0.6 },
};

/** Generic disabled look (applied with variantDisabledStyles) */
export const disabledStyle = {
  backgroundColor: "#e9ecef",
  color: "#6c757d",
  border: "1px solid #dee2e6",
  cursor: "not-allowed",
  opacity: 0.65,
};

/** Loading state (cursor + optional opacity) */
export const loadingStyle = {
  cursor: "wait",
};

export const SIZES = ["sm", "md", "lg"];

export const VARIANTS = [
  "primary",
  "primaryOutline",
  "default",
  "defaultOutline",
  "secondary",
  "dark",
  "darkOutline",
  "download",
  "downloadOutline",
  "success",
  "successOutline",
  "confirmation",
  "confirmationOutline",
  "save",
  "saveOutline",
  "reject",
  "rejectOutline",
  "transfer",
  "transferOutline",
  "discard",
  "request",
  "detailPage",
  "history",
  "ghost",
  "danger",
  "imageView",
];
